import * as d3 from 'd3';
import { GAS_LIMIT } from '../utils/constants.js';

const format8 = d3.format(".8f")

function textTween(a, b) {
    const i = d3.interpolateNumber(a, b);
    return function (t) {
        this.textContent = format8(i(t));
    };
}

export default class BlockBidUI {
    static BUILDER_DISPLAY_LIMIT = 10
    static BAR_SIZE = 80
    static UPDATE_INTERVAL = 1000
    static TRANSITION_DURATION = 500

    constructor(element) {
        this.padding = { top: 5, right: 5, bottom: 5, left: 0 };
        var parentDiv = document.querySelector(element); // Replace with the ID of your parent div
        var rect = parentDiv.getBoundingClientRect();
        this.height = rect.height;
        this.width = rect.width;

        this.svg = d3.select(element).append("svg")
            .attr("preserveAspectRatio", "xMinYMin meet")
            .attr("viewBox", `0 0 ${this.width} ${this.height}`)

        this.x = d3.scaleLinear([0, 1], [this.padding.left, this.width - this.padding.right])
        this.y = d3.scaleBand()
            .domain(d3.range(BlockBidUI.BUILDER_DISPLAY_LIMIT + 1))
            .rangeRound([this.padding.top, this.height - this.padding.bottom])
            .padding(0.2)

        this.vline = this.svg.append('line')
            .attr('x1', this.x(0))
            .attr('x2', this.x(0))
            .attr('y1', this.padding.top)
            .attr('y2', this.height - this.padding.bottom)
            .attr('stroke-width', '2')
            .attr('stroke', '#d3d3d3')

        this.hline1 = this.svg.append('line')
            .attr('x1', this.x(0))
            .attr('x2', this.x(this.width - this.padding.right))
            .attr('y1', this.padding.top)
            .attr('y2', this.padding.top)
            .attr('stroke-width', '1.5')
            .attr('stroke', '#d3d3d3')

        /*
this.hline2 = this.svg.append('line')
    .attr('x1', this.x(0))
    .attr('x2', this.x(this.width - this.padding.right))
    .attr('y1', this.height - this.padding.bottom)
    .attr('y2', this.height - this.padding.bottom)
    .attr('stroke-width', '1')
    .attr('stroke', '#d3d3d3')
    */

        // clear
        this.clear = false
        // reset ui
        this.reset()
    }

    setBlockInfo = (block_info) => {
        if (block_info.submissions !== undefined && block_info.submissions.length > 1) {
            var sub = block_info.submissions[block_info.submissions.length - 1]
            this.setBuilderMap(sub.best_by_builder, sub.bids_by_relay)
        }
    }

    setBuilderMap = (builderMap, bids_by_relay) => {
        const data = [];
        for (const [key, value] of Object.entries(builderMap)) {
            var bids = 0
            if (bids_by_relay !== undefined) {
                for (const [rel, bidsMap] of Object.entries(bids_by_relay)) {
                    if (bidsMap.hasOwnProperty(value.builder)) {
                        bids += bidsMap[value.builder]
                    }
                }
            }
            data.push({ builder: value.builder, value: value.value, gas_usage: value.gas_used / GAS_LIMIT, num_tx: value.num_tx, bids: bids });
        }
        data.sort((a, b) => d3.descending(+a.value, +b.value));
        for (let i = 0; i < data.length; ++i) data[i].rank = Math.min(BlockBidUI.BUILDER_DISPLAY_LIMIT, i);

        const transition = this.svg.transition()
            .duration(BlockBidUI.TRANSITION_DURATION)
            .ease(d3.easeLinear);

        this.x.domain([0, data[0].value]);

        this.updateBars(data, transition);
        this.updateLabels(data, transition);
    }

    start = () => {
        if (this.interval === undefined) {
            this.interval = setInterval(this.updateUi, BlockBidUI.UPDATE_INTERVAL);
        }
    }

    pause = () => {
        if (this.interval !== undefined) {
            clearInterval(this.interval);
            this.interval = undefined
        }
    }

    newBidSummary = (bid_summary) => {
        //console.log(bid_summary)
        if (this.bid_summary !== undefined && bid_summary.block < this.bid_summary.block) {
            // old bid summary
            return
        }
        if (this.bid_summary !== undefined && bid_summary.block > this.bid_summary.block) {
            this.clear = true
        }
        this.bid_summary = bid_summary
    }

    updateUi = async () => {
        if (this.bid_summary === undefined) {
            return
        }
        if (this.clear) {
            //console.log("clear")
            this.reset()
            this.clear = false
        }

        var data = this.prepareData(this.bid_summary.best_by_builder, this.bid_summary.bids_by_relay)
        if (data.length === 0) {
            return
        }

        const transition = this.svg.transition()
            .duration(BlockBidUI.TRANSITION_DURATION)
            .ease(d3.easeLinear);

        // Extract the top bar’s value.
        //console.log(data[0].value)
        this.x.domain([0, data[0].value]);

        //updateAxis(data, transition);
        this.updateBars(data, transition);
        this.updateLabels(data, transition);

        //updateLabels(data, transition);
        //updateTicker(data, transition);

        //invalidation.then(() => this.svg.interrupt());
        await transition.end();
    }

    prepareData(builderMap, bids_by_relay) {
        const data = [];
        for (const [key, value] of Object.entries(builderMap)) {
            var bids = 0
            if (bids_by_relay !== undefined) {
                for (const [rel, bidsMap] of Object.entries(bids_by_relay)) {
                    if (bidsMap.hasOwnProperty(value.builder)) {
                        bids += bidsMap[value.builder]
                    }
                }
            }
            data.push({ builder: value.builder, value: value.value, gas_usage: value.gas_used / GAS_LIMIT, num_tx: value.num_tx, bids: bids });
        }
        data.sort((a, b) => d3.descending(+a.value, +b.value));
        for (let i = 0; i < data.length; ++i) data[i].rank = Math.min(BlockBidUI.BUILDER_DISPLAY_LIMIT, i);
        return data;
    }

    updateBars = (data, transition) => {
        this.valuebar = this.valuebar
            .data(data.slice(0, BlockBidUI.BUILDER_DISPLAY_LIMIT), d => d.builder)
            .join(
                enter => enter.append("rect")
                    .attr("fill", "#26a6ed")
                    .attr("height", this.y.bandwidth())
                    .attr("x", this.x(0))
                    .attr("y", d => this.y(d.rank))
                    .attr("width", d => this.x(d.value) - this.x(0)),
                update => update,
                exit => exit.transition(transition).remove()
                    .attr("y", d => this.y(d.rank))
                    .attr("width", d => this.x(d.value) - this.x(0))
            )
            .call(bar => bar.transition(transition)
                .attr("y", d => this.y(d.rank))
                .attr("width", d => this.x(d.value) - this.x(0)));

        this.gasbar = this.gasbar
            .data(data.slice(0, BlockBidUI.BUILDER_DISPLAY_LIMIT), d => d.builder)
            .join(
                enter => enter.append("rect")
                    .attr("fill", "#35e9b3")
                    .attr("height", this.y.bandwidth() / 8)
                    .attr("x", this.x(0))
                    .attr("y", d => this.y(d.rank))
                    .attr("width", d => (this.x(d.value) - this.x(0)) * d.gas_usage),
                update => update,
                exit => exit.transition(transition).remove()
                    .attr("y", d => this.y(d.rank))
                    .attr("width", d => (this.x(d.value) - this.x(0)) * d.gas_usage)
            )
            .call(bar => bar.transition(transition)
                .attr("y", d => this.y(d.rank))
                .attr("width", d => (this.x(d.value) - this.x(0)) * d.gas_usage));
    }

    updateLabels = (data, transition) => {
        this.label = this.label
            .data(data.slice(0, BlockBidUI.BUILDER_DISPLAY_LIMIT), d => d.builder)
            .join(
                enter => enter.append("text")
                    .attr("transform", d => `translate(${this.x(d.value)},${this.y(d.rank)})`)
                    .attr("y", this.y.bandwidth() / 2)
                    .attr("x", -6)
                    .attr("dy", "-0.25em")
                    .text(d => d.builder)
                    .call(text => text.append("tspan")
                        .attr("fill-opacity", 0.7)
                        .attr("font-weight", "normal")
                        .attr("x", -6)
                        .attr("dy", "1.15em")),
                update => update,
                exit => exit.transition(transition).remove()
                    .attr("transform", d => `translate(${this.x(d.value)},${this.y(d.rank)})`)
                    .call(g => g.select("tspan").tween("text", d => textTween(d.value, d.value)))
            )
            .call(bar => bar.transition(transition)
                .attr("transform", d => `translate(${this.x(d.value)},${this.y(d.rank)})`)
                .call(g => g.select("tspan").tween("text", d => textTween(d.value, d.value))))

        this.bids = this.bids
            .data(data.slice(0, BlockBidUI.BUILDER_DISPLAY_LIMIT), d => d.builder)
            .join(
                enter => enter.append("text")
                    .attr("height", this.y.bandwidth())
                    .attr("x", this.x(0) + 2)
                    .attr("y", d => this.y(d.rank) + this.y.bandwidth() / 1.5)
                    .text(d => d.bids),
                update => update,
                exit => exit.transition(transition).remove()
                    .attr("y", d => this.y(d.rank) + this.y.bandwidth() / 1.5)
                    .text(d => d.bids)
            )
            .call(bar => bar.transition(transition)
                .attr("y", d => this.y(d.rank) + this.y.bandwidth() / 1.5)
                .text(d => d.bids));
    }

    reset = () => {
        //console.log("reset")

        if (this.valuebar !== undefined) {
            this.valuebar.remove()
        }
        this.valuebar = this.svg.append("g")
            .attr("fill-opacity", 0.8)
            .selectAll("rect");

        if (this.gasbar !== undefined) {
            this.gasbar.remove()
        }
        this.gasbar = this.svg.append("g")
            .attr("fill-opacity", 0.9)
            .selectAll("rect");

        if (this.label !== undefined) {
            this.label.remove()
        }
        this.label = this.svg.append("g")
            .style("font-family", "Courier,Courier New,slab-serif")
            .style("font-weight", "bold")
            .style("font-size", "10px")
            .style("font-variant-numeric", "tabular-nums")
            .attr("text-anchor", "end")
            .selectAll("text");

        if (this.bids !== undefined) {
            this.bids.remove()
        }
        this.bids = this.svg.append("g")
            .style("font-family", "Courier,Courier New,slab-serif")
            .style("font-weight", "bold")
            .style("font-size", "10px")
            .style("font-variant-numeric", "tabular-nums")
            .attr("text-anchor", "start")
            .selectAll("text");
    }
}